import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { SnapList, SnapItem, useScroll, useDragToScroll } from 'react-snaplist-carousel';
import { debounce } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import {
  CitiesListContextProvider,
  useCitiesListContext,
} from '@/features/marketplace/hooks/use-get-cities';
import { DEFAULT_CONTENT_WIDTH_IN_NUM } from '../../../../../../../constants';
import { CityCard } from '../city-card';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_LARGE,
  MOBILE_PADDING,
  VIEWPORT_PADDING_PERCENTAGE,
  CARD_GAP,
  SCROLL_DEBOUNCE_DELAY,
  SCROLL_STATE_UPDATE_DELAY,
} from '../../constants';

interface CitiesHorizontalCarouselProps {
  onScrollStateChange?: (canScrollLeft: boolean, canScrollRight: boolean) => void;
}

const CitiesHorizontalCarousel: React.FC<CitiesHorizontalCarouselProps> = ({
  onScrollStateChange,
}) => {
  const snapListRef = useRef<HTMLDivElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const { t } = useTranslation();

  const {
    windowDimensions: { width: screenWidth },
  } = useWindowDimensions();

  const { citiesList, fetchCitiesList } = useCitiesListContext()!;

  const goToChildren = useScroll({ ref: snapListRef });
  useDragToScroll({ ref: snapListRef });

  // Calculate responsive horizontal padding
  const paddingHorizontal = useMemo(() => {
    // Mobile (< 576px): calc(100% - 48px) container = 24px padding
    if (screenWidth < BREAKPOINT_MOBILE) {
      return MOBILE_PADDING;
    }

    // SM+ (576px - 1624px): 80% width container = 10% padding on each side
    if (screenWidth < BREAKPOINT_LARGE) {
      return screenWidth * VIEWPORT_PADDING_PERCENTAGE;
    }

    // Large screens (≥ 1625px): max-width 1300px centered
    return (screenWidth - DEFAULT_CONTENT_WIDTH_IN_NUM) / 2;
  }, [screenWidth]);

  // Update scroll state and notify parent
  const updateScrollState = useCallback(() => {
    const el = snapListRef.current;
    if (!el) return;

    const canLeft = el.scrollLeft > 0;
    const canRight = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth;

    // Notify parent component of scroll state changes
    onScrollStateChange?.(canLeft, canRight);
  }, [onScrollStateChange]);

  // Debounced scroll handler for better performance
  const debouncedHandleScroll = useMemo(
    () => debounce(updateScrollState, SCROLL_DEBOUNCE_DELAY),
    [updateScrollState]
  );

  // Scroll to a specific direction
  const scrollToDirection = useCallback(
    (direction: 'left' | 'right') => {
      const el = snapListRef.current;
      if (!el) return;

      const scrollAmount = direction === 'left' ? -el.clientWidth : el.clientWidth;
      el.scrollTo({ left: el.scrollLeft + scrollAmount, behavior: 'smooth' });

      // Update scroll state after animation completes
      setTimeout(updateScrollState, SCROLL_STATE_UPDATE_DELAY);
    },
    [updateScrollState]
  );

  // SSR-safe mounting effect
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Setup scroll listener
  useEffect(() => {
    const el = snapListRef.current;
    if (!el || !isMounted) return;

    el.addEventListener('scroll', debouncedHandleScroll);

    // Initial state calculation
    updateScrollState();

    return () => {
      el.removeEventListener('scroll', debouncedHandleScroll);
      debouncedHandleScroll.cancel(); // Cancel any pending debounced calls
    };
  }, [debouncedHandleScroll, updateScrollState, isMounted]);

  // Recalculate scroll state when cities list or screen width changes
  useEffect(() => {
    if (!isMounted) return;

    // Use requestAnimationFrame for better timing with layout changes
    const rafId = requestAnimationFrame(() => {
      updateScrollState();
    });

    return () => cancelAnimationFrame(rafId);
  }, [citiesList.length, screenWidth, updateScrollState, isMounted]);

  // Attach scroll methods to a global ref that parent can access
  // This is done via a custom event system for better decoupling
  useEffect(() => {
    if (!isMounted || typeof window === 'undefined') return;

    const handleScrollCommand = (event: CustomEvent<{ direction: 'left' | 'right' }>) => {
      scrollToDirection(event.detail.direction);
    };

    window.addEventListener('cities-carousel-scroll' as any, handleScrollCommand as EventListener);

    return () => {
      window.removeEventListener(
        'cities-carousel-scroll' as any,
        handleScrollCommand as EventListener
      );
    };
  }, [scrollToDirection, isMounted]);

  // Loading state
  if (fetchCitiesList.isLoading) {
    return (
      <div className="mb-7 mt-10 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-2 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
          <p className="text-sm text-text-secondary">
            {t('js.features.marketplace.home.section2.cities_list.loading')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (fetchCitiesList.isError) {
    return (
      <div className="mb-7 mt-10 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-600">
            {t('js.features.marketplace.home.section2.cities_list.error')}
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (citiesList.length === 0) {
    return (
      <div className="mb-7 mt-10 flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-text-secondary">
            {t('js.features.marketplace.home.section2.cities_list.empty')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <SnapList
      ref={snapListRef as any}
      direction="horizontal"
      className="mb-7 mt-10"
    >
      {citiesList.map((city, idx) => (
        <SnapItem
          key={city.id}
          margin={{
            left: idx === 0 ? `${paddingHorizontal}px` : `${CARD_GAP}px`,
            right: idx === citiesList.length - 1 ? `${paddingHorizontal}px` : `${CARD_GAP}px`,
          }}
          snapAlign="center"
        >
          <CityCard cityId={city.id} onClick={() => goToChildren(idx)} />
        </SnapItem>
      ))}
    </SnapList>
  );
};

const CitiesList: React.FC<CitiesHorizontalCarouselProps> = (props) => {
  return (
    <CitiesListContextProvider>
      <CitiesHorizontalCarousel {...props} />
    </CitiesListContextProvider>
  );
};

export { CitiesList };
